import React, { useEffect, useRef, useCallback, useState } from "react";
import { useMessageContext } from "provider/MessageProvider";
import apiCall from "components/Api/api";
import { useAuth } from "provider/AuthContext";
import { useNavigate } from "react-router-dom";
import { handleError } from "components/Api/handleError";
import debounce from "lodash.debounce";
import ChatMessageItem from "./ChatMessageItem";
import { faArrowDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatMessageTime, groupMessagesByDate } from "components/Api/common";
import { useScreenSize } from "provider/ScreenSizeProvider";

const ChatMessages = ({height}) => {
    const {
        msg,
        msgLoading,
        msgPageIndex,
        initialPageIndex,
        newMessageAdded,
        msgSummary,
        messageDispatch,
        activeUser,
    } = useMessageContext();
    const { logout } = useAuth();
    const navigate = useNavigate();
    const { isMobileView } = useScreenSize();
    const [visible, setVisible] = useState(false);
    const containerRef = useRef(null);
    const fetchedPagesRef = useRef(new Set());
    const [loading, setLoading] = useState(false);


    const loadOlderMessages = useCallback(async () => {
        const nextPage = msgPageIndex + 1;
        if (
            fetchedPagesRef.current.has(nextPage) ||
            nextPage > (msgSummary?.total_page || 1)
        ) {
            return;
        }

        fetchedPagesRef.current.add(nextPage);


        try {
            setLoading(true);
            const prevScrollHeight = containerRef.current.scrollHeight;

            const response = await apiCall({
                url: `chat/messages/${activeUser.chat_hash}?page=${nextPage}`,
                method: "GET",
            });

            if (response.status !== 200) throw new Error(`Unexpected status: ${response.status}`);

            const apiData = response.data;

            if (apiData?.errors) {
                messageDispatch({ type: "SET_MESSAGE_ERROR", payload: apiData.errors });
                return;
            }

            const newMessages = apiData?.chat_messages?.data || [];

            messageDispatch({
                type: "APPEND_MESSAGE_TOP",
                payload: newMessages,
            });

            messageDispatch({
                type: "MESSAGE_PAGE_INDEX",
                payload: nextPage,
            });

            // Maintain current scroll position
            setTimeout(() => {
                if (!containerRef.current) return;
                const newScrollHeight = containerRef.current.scrollHeight;
                containerRef.current.scrollTop = newScrollHeight - prevScrollHeight;
            }, 0);

        } catch (error) {
            console.error("Older message fetch error:", error);
            messageDispatch({
                type: "SET_MESSAGE_ERROR",
                payload: "Failed to load older messages.",
            });
            handleError(error, logout, navigate);
        } finally {
            setLoading(false);
        }
    }, [msgPageIndex, msgSummary?.total_page, activeUser?.chat_hash]);


    useEffect(() => {

        const container = containerRef.current;
        if (!container) return;
        if (initialPageIndex <= 3) {
            scrollToBottom();
        }
        // Create a stable debounced function
        const debounced = debounce(() => {
            if (msgLoading) return
            const { scrollTop, scrollHeight, clientHeight } = container;
            const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
            setVisible(distanceFromBottom > 300);

            const threshold = container.clientHeight * 0.35;

            if (scrollTop <= threshold) {
                console.log("Reached top 35%, loading older messages");
                loadOlderMessages();
            }
        }, 200);

        container.addEventListener("scroll", debounced);

        return () => {
            container.removeEventListener("scroll", debounced);
            debounced.cancel(); // cancel the debounce to clean up
        };
    }, [loadOlderMessages, msgLoading]);


    useEffect(() => {
        // Reset scroll and fetched page state when a new chat is opened
        fetchedPagesRef.current.clear();
        if (containerRef.current) {
            containerRef.current.scrollTop = containerRef.current.scrollHeight;
        }
    }, [activeUser?.chat_hash]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        //for initila data fetch
        if (initialPageIndex === msgPageIndex && !newMessageAdded) {
            const targetScrollTop = container.clientHeight
            container.scrollTop = targetScrollTop;
            return
        }
        if (msgPageIndex === 1 || msgPageIndex === 2 || msgPageIndex === 3) {
            scrollToBottom();
        }
        // when new message added
        if (newMessageAdded) {
            scrollToBottom();
            messageDispatch({
                type: "NEW_MSG_ADDED",
                payload: false
            });
            return
        }
        // Calculate 32vh in pixels relative to the container
        const targetScrollTop = container.clientHeight * (32 / 64); // since container is 64vh

        container.scrollTop = targetScrollTop;
    }, [msg?.length]);




    const scrollToBottom = () => {
        const container = containerRef.current;
        if (!container) return;

        container.scrollTo({
            top: container.scrollHeight,
            behavior: "smooth",
        });
    };

 const dynamicHeight = height - (isMobileView ? 360 : 300);

    return (
        <div style={{ position: "relative" }}>
            <div
                ref={containerRef}
                className="chat-scroll-container"
                style={{
                    height: `${dynamicHeight}px`,
                    maxHeight: `${dynamicHeight - 12}px`,
                    overflowY: "auto",
                    paddingTop: "6px",
                    position: "relative",

                }}
                 
            >
                {activeUser?.chat_hash &&
                    <>
                        {msgLoading ?
                            <div className="d-flex justify-content-center">
                                <div
                                    className="spinner-border loading-spinner"
                                    role="status"
                                >
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                            :
                            <>
                                {loading
                                    &&
                                    <div className="d-flex justify-content-center">
                                        <div
                                            className="spinner-border loading-spinner"
                                            role="status"
                                        >
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                }
                                {Object.entries(groupMessagesByDate(msg)).map(([groupLabel, messages], index) => (
                                    <div key={index}>
                                        <div className="chat-date-divider text-center my-3">
                                            <span className="chat-date-label-group px-3 py-1">
                                                {groupLabel}
                                            </span>
                                        </div>
                                        {messages.map((message, key) => (
                                            <ChatMessageItem
                                                key={message.id || key}
                                                data={message}
                                                message={message?.message || ''}
                                                isSentByYou={message?.is_message_sent_by_you}
                                                isSystemGenrated={message?.is_system_generated}
                                                time={formatMessageTime(Number(message?.message_datetime))}
                                            />
                                        ))}
                                    </div>
                                ))}

                            </>
                        }
                    </>
                }
            </div>
            {visible && (
                <button
                    className="btn-top chat-back-to-top"
                    onClick={scrollToBottom}

                    title="Back to top"
                >
                    <FontAwesomeIcon
                        icon={faArrowDown}
                        color="#000000"
                        size="sm"
                    />
                </button>
            )}
        </div>
    );
};

export default ChatMessages;
